/**
 * useSearchActive — true when the URL on `/` represents a submitted
 * search, false otherwise (VOY-477).
 *
 * The discriminator is the URL, NOT useSearchState's transient fields.
 * Watching `state.query` directly was wrong: every keystroke in the
 * composer updates `state.query`, which would have flipped this to
 * true and remounted State B on the first character typed, dropping
 * focus from the input. (PR #280 review fix.)
 *
 * Listens to `popstate` so back/forward navigation re-derives the flag.
 * For programmatic `pushState`/`replaceState` calls inside LandingV6,
 * we expose `setSearchActive` via the same hook so the caller can keep
 * the flag in sync with the URL it just wrote (history doesn't fire
 * popstate for same-tab pushState; we have to update locally).
 */

import { useCallback, useEffect, useState } from 'react'

const SEARCH_PARAMS = ['q', 'destination', 'origin', 'savedSearchId', 'autoSearch']

function urlIsSearch(search: string): boolean {
  if (!search) return false
  const params = new URLSearchParams(search)
  return SEARCH_PARAMS.some((key) => {
    const v = params.get(key)
    return v !== null && v !== ''
  })
}

export interface UseSearchActiveApi {
  searchActive: boolean
  /** Programmatic setter for callers that just wrote to history.pushState. */
  setSearchActive: (next: boolean) => void
}

export function useSearchActive(): UseSearchActiveApi {
  const [searchActive, setSearchActive] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return urlIsSearch(window.location.search)
  })

  useEffect(() => {
    const handlePop = () => {
      setSearchActive(urlIsSearch(window.location.search))
    }
    window.addEventListener('popstate', handlePop)
    return () => window.removeEventListener('popstate', handlePop)
  }, [])

  return {
    searchActive,
    setSearchActive: useCallback((next: boolean) => setSearchActive(next), []),
  }
}
